import { Modal } from './modal.jsx'
import type { JSX } from 'react'

/**
 * Asks before hiding a customer or an account.
 *
 * Not before hiding an *instance*, and the asymmetry is IM-79's: gating the
 * ordinary, trivially reversible act trains people to dismiss the dialog, which
 * is how a confirmation stops confirming anything. A customer is a whole tree,
 * and this is the only place that says so.
 *
 * Deliberately lighter than `confirm-destroy.tsx` — no typing the name back.
 * That one guards an act that reaches a customer's real infrastructure and
 * cannot be undone from this side; this one guards a mark on a row, with a
 * Restore button beside it afterwards. Matching its friction would be claiming
 * a stake this act does not have.
 *
 * It is **not a control**: `authorize` checks `records:manage` regardless and
 * records the decision either way, and anyone reaching the endpoint skips this.
 */
export const ConfirmHide = ({
  what,
  name,
  onConfirm,
  onCancel,
}: {
  /** 'customer' or 'account'. */
  readonly what: string
  readonly name: string
  readonly onConfirm: () => void
  readonly onCancel: () => void
}): JSX.Element => {
  return (
    <Modal title={`Hide this ${what}?`} onCancel={onCancel}>
      <p>
        <strong>{name}</strong> stops appearing in the lists. Nothing is deleted and nothing in the
        customer&rsquo;s cloud account changes.
      </p>
      {what === 'customer' ? (
        <p className="hint">
          Its accounts and instances go with it. Restoring brings the whole lot back.
        </p>
      ) : what === 'region' ? (
        /*
         * The one consequence nothing else on the screen states.
         *
         * Hiding a region conceals where Terraform state lives — the bucket is
         * `Retain`, so it and everything in it survive, and this control plane
         * has no way to delete them even if that were wanted. Someone later
         * wondering where an instance's state went should find the answer by
         * turning on Show hidden, not by reading the code.
         */
        <p className="hint">
          The Terraform state bucket and everything in it stay exactly where they are — Qrvey simply
          stops showing this region. It is the only way to clear one, because the state bucket
          outlives its stack by design.
        </p>
      ) : (
        <p className="hint">Its instances go with it. Restoring brings them back.</p>
      )}
      <div className="actions">
        <button type="button" className="ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" onClick={onConfirm}>
          Hide {what}
        </button>
      </div>
    </Modal>
  )
}
